<div class="space-y-5">

  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h2 class="text-2xl font-bold text-gray-800">Academic Years</h2>
      <p class="text-sm text-gray-500 mt-1">
        {{ filteredYears.length }} year{{ filteredYears.length === 1 ? '' : 's' }} found
      </p>
    </div>
    <button
      (click)="openAddModal()"
      class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity"
    >
      <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
      </svg>
      Add Year
    </button>
  </div>

  <!-- Search -->
  <div class="relative">
    <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
    </svg>
    <input
      type="text"
      [(ngModel)]="searchQuery"
      (ngModelChange)="onSearch()"
      placeholder="Search by name..."
      class="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"
    />
  </div>

  <!-- Loader -->
  <app-loader-component [loading]="loading" message="Loading academic years..."></app-loader-component>

  <!-- Error -->
  <div *ngIf="errorMsg && !loading" class="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
    <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/>
    </svg>
    {{ errorMsg }}
  </div>

  <!-- Empty -->
  <div *ngIf="!loading && !errorMsg && filteredYears.length === 0" class="flex flex-col items-center justify-center py-16 text-center">
    <svg class="w-10 h-10 text-gray-300 mb-3" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
    </svg>
    <p class="text-sm font-medium text-gray-600">No academic years found</p>
    <p class="text-xs text-gray-400 mt-1">Add your first academic year to get started.</p>
  </div>

  <!-- Cards grid -->
  <div *ngIf="!loading && pagedYears.length > 0" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
    <div
      *ngFor="let year of pagedYears"
      class="relative bg-white rounded-2xl border border-gray-200 p-5 flex flex-col gap-4 hover:shadow-md transition-shadow"
    >

      <!-- Current badge -->
      <div *ngIf="year.current" class="absolute top-4 right-4">
        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 border border-green-200 text-xs font-semibold text-green-700">
          <span class="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
          Current
        </span>
      </div>

      <!-- Year icon + name -->
      <div class="flex items-center gap-3 pr-16">
        <div class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <svg class="w-5 h-5 text-primary" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
          </svg>
        </div>
        <div class="min-w-0">
          <p class="text-base font-bold text-gray-800 truncate">{{ year.name }}</p>
          <p *ngIf="getDurationMonths(year.startDate, year.endDate)" class="text-xs text-gray-400">{{ getDurationMonths(year.startDate, year.endDate) }}</p>
        </div>
      </div>

      <!-- Date range -->
      <div class="space-y-2">
        <div class="flex items-center justify-between text-xs">
          <span class="text-gray-400 font-medium">Start</span>
          <span class="text-gray-700 font-semibold">{{ formatDate(year.startDate) }}</span>
        </div>
        <div class="w-full h-px bg-gray-100"></div>
        <div class="flex items-center justify-between text-xs">
          <span class="text-gray-400 font-medium">End</span>
          <span class="text-gray-700 font-semibold">{{ formatDate(year.endDate) }}</span>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-2 pt-1 border-t border-gray-100">

        <!-- Set as current -->
        <button
          *ngIf="!year.isCurrent"
          (click)="openSetCurrentModal(year)"
          class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 hover:border-green-300 hover:text-green-700 cursor-pointer transition-colors"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
          </svg>
          Set Current
        </button>

        <div *ngIf="year.current" class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-50 border border-green-200 text-xs font-semibold text-green-700 select-none">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
          </svg>
          Active Year
        </div>

        <!-- Delete -->
        <button
          (click)="openDeleteModal(year)"
          title="Delete year"
          class="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 cursor-pointer transition-colors"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
          </svg>
        </button>

      </div>
    </div>
  </div>

  <!-- Pagination -->
  <div *ngIf="filteredYears.length > size" class="flex items-center justify-between pt-2">
    <span class="text-xs text-gray-400">
      Showing {{ getMinValue(page * size + size, filteredYears.length) }} of {{ filteredYears.length }}
    </span>
    <div class="flex items-center gap-1">
      <button
        (click)="prevPage()"
        [disabled]="!hasPrev"
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
        </svg>
        Prev
      </button>
      <span class="px-3 py-1.5 text-sm text-gray-600">{{ page + 1 }} / {{ totalPages }}</span>
      <button
        (click)="nextPage()"
        [disabled]="!hasNext"
        class="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 font-medium cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Next
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>
  </div>

</div>


<!-- ══════════════════════════════════════ ADD MODAL -->
<div *ngIf="showAddModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeAddModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">

    <div class="flex items-center justify-between">
      <h3 class="text-lg font-bold text-gray-800">Add Academic Year</h3>
      <button (click)="closeAddModal()" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      </button>
    </div>

    <div *ngIf="addError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ addError }}
    </div>

    <div class="space-y-4">
      <div class="space-y-1.5">
        <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Name *</label>
        <input
          type="text"
          [(ngModel)]="addForm.name"
          placeholder="e.g. 2025 / 2026"
          class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-primary transition-colors"
        />
      </div>
      <div class="grid grid-cols-2 gap-3">
        <div class="space-y-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">Start Date *</label>
          <input
            type="date"
            [(ngModel)]="addForm.startDate"
            class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
          />
        </div>
        <div class="space-y-1.5">
          <label class="text-xs font-semibold uppercase tracking-wider text-gray-400">End Date *</label>
          <input
            type="date"
            [(ngModel)]="addForm.endDate"
            class="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>
      <label class="flex items-center gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors select-none">
        <input
          type="checkbox"
          [(ngModel)]="addForm.setAsCurrent"
          class="w-4 h-4 rounded accent-primary cursor-pointer"
        />
        <div>
          <p class="text-sm font-semibold text-gray-700">Set as current year</p>
          <p class="text-xs text-gray-400">Makes this the active academic year across the system.</p>
        </div>
      </label>
    </div>

    <div class="flex items-center justify-end gap-3 pt-1">
      <button (click)="closeAddModal()" [disabled]="addLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="submitAdd()" [disabled]="addLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="addLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        {{ addLoading ? 'Saving...' : 'Add Year' }}
      </button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ SET CURRENT CONFIRM MODAL -->
<div *ngIf="showSetCurrentModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeSetCurrentModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">

    <div class="flex items-start gap-4">
      <div class="w-10 h-10 rounded-xl bg-green-50 border border-green-200 flex items-center justify-center flex-shrink-0">
        <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
        </svg>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-800">Set as Current Year</h3>
        <p class="text-sm text-gray-600 mt-1">
          Set <span class="font-semibold">{{ selectedYear?.name }}</span> as the active academic year? Any previously current year will be replaced.
        </p>
      </div>
    </div>

    <div *ngIf="setCurrentError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ setCurrentError }}
    </div>

    <div class="flex items-center justify-end gap-3">
      <button (click)="closeSetCurrentModal()" [disabled]="setCurrentLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="confirmSetCurrent()" [disabled]="setCurrentLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="setCurrentLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        {{ setCurrentLoading ? 'Applying...' : 'Confirm' }}
      </button>
    </div>
  </div>
</div>


<!-- ══════════════════════════════════════ DELETE CONFIRM MODAL -->
<div *ngIf="showDeleteModal" class="fixed inset-0 z-50 flex items-center justify-center p-4" (click)="closeDeleteModal()">
  <div class="absolute inset-0 bg-black/40"></div>
  <div class="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-200 p-6 space-y-5" (click)="$event.stopPropagation()">

    <div class="flex items-start gap-4">
      <div class="w-10 h-10 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
        <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"/>
        </svg>
      </div>
      <div>
        <h3 class="text-base font-bold text-gray-800">Delete Academic Year</h3>
        <p class="text-sm text-gray-600 mt-1">
          Are you sure you want to delete <span class="font-semibold">{{ selectedYear?.name }}</span>? This action cannot be undone.
        </p>
      </div>
    </div>

    <div *ngIf="deleteError" class="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600">
      <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 8v4m0 4h.01"/></svg>
      {{ deleteError }}
    </div>

    <div class="flex items-center justify-end gap-3">
      <button (click)="closeDeleteModal()" [disabled]="deleteLoading" class="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Cancel</button>
      <button (click)="confirmDelete()" [disabled]="deleteLoading" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed">
        <svg *ngIf="deleteLoading" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        {{ deleteLoading ? 'Deleting...' : 'Delete' }}
      </button>
    </div>
  </div>
</div>